import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit";
import { RootState } from "_shared/_stores/redux-store";
import Session from "users/__session/session";
import TeamsAPIService, { InvalidVendorTeamError } from "work/work-agreements/teams/_api/teams-api-service";
import { VendorTeamJSON } from "work/work-agreements/teams/vendor-team/_schemas/vendor-team-schema";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

export type VendorTeamsStoreState = {
  byQuery: {
    entries: Record<string, VendorTeamJSON[]>;
    loading: Record<string, boolean>;
    error: Record<string, InvalidVendorTeamError | null>;
  };
  interfaceQueries: {
    entries: Record<string, { workingForMe: boolean }>;
  };
};

const initialState: VendorTeamsStoreState = {
  byQuery: {
    entries: {},
    loading: {},
    error: {},
  },
  interfaceQueries: {
    entries: {},
  },
};

export const populateTeamsByQuery = createAsyncThunk(
  "vendorTeams/populateTeamsByQuery",
  async (
    {
      session,
      workingForMe,
      interfaceName,
    }: {
      session: Session;
      workingForMe: boolean;
      interfaceName: string;
    },
    thunkAPI,
  ) => {
    const apiService = new TeamsAPIService(session);
    const abortController = new AbortController();
    const onAbort = () => abortController.abort();
    thunkAPI.signal.addEventListener("abort", onAbort);
    try {
      const data = await apiService.getActiveTeamsAsync(workingForMe, abortController);
      return { interfaceName, workingForMe, data };
    } catch (error) {
      return thunkAPI.rejectWithValue(new InvalidVendorTeamError("populateTeamsByQuery", error));
    } finally {
      thunkAPI.signal.removeEventListener("abort", onAbort);
    }
  },
);

export const updateTeamsInterfaceQuery = createAsyncThunk(
  "vendorTeams/updateTeamsInterfaceQuery",
  async (
    {
      interfaceName,
      workingForMe,
    }: {
      interfaceName: string;
      workingForMe: boolean;
    },
    _thunkAPI,
  ) => {
    return { interfaceName, workingForMe };
  },
);

const vendorTeamsSlice = createSlice({
  name: "vendorTeams",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(populateTeamsByQuery.pending, (state, action) => {
      const { interfaceName, workingForMe } = action.meta.arg;
      const key = `${interfaceName}:${workingForMe}`;
      state.byQuery.loading[key] = true;
      state.byQuery.error[key] = null;
    });
    builder.addCase(populateTeamsByQuery.fulfilled, (state, action) => {
      const { interfaceName, workingForMe, data } = action.payload;
      const key = `${interfaceName}:${workingForMe}`;
      state.byQuery.entries[key] = data;
      state.byQuery.loading[key] = false;
      state.byQuery.error[key] = null;
    });
    builder.addCase(populateTeamsByQuery.rejected, (state, action) => {
      const { interfaceName, workingForMe } = action.meta.arg;
      const key = `${interfaceName}:${workingForMe}`;
      state.byQuery.loading[key] = false;
      state.byQuery.error[key] = action.payload as InvalidVendorTeamError;
    });
    builder.addCase(updateTeamsInterfaceQuery.fulfilled, (state, action) => {
      const { interfaceName, workingForMe } = action.payload;
      state.interfaceQueries.entries[interfaceName] = { workingForMe };
    });
  },
});

const selectTeamsQueryByInterface = createSelector(
  [
    (state: RootState) => state.vendorTeams.interfaceQueries.entries,
    (_: RootState, interfaceName: string) => interfaceName,
  ],
  (entries, interfaceName) => entries[interfaceName],
);
export const getTeamsQueryByInterface = (interfaceName: string) => (state: RootState) =>
  selectTeamsQueryByInterface(state, interfaceName);

const selectTeamsByQuery = createSelector(
  [
    (state: RootState) => state.vendorTeams.byQuery.entries,
    (_: RootState, interfaceName: string, workingForMe: boolean) => `${interfaceName}:${workingForMe}`,
  ],
  (entries, key) => {
    const arr = entries[key];
    if (arr === undefined) return;
    return arr.map((json) => VendorTeam.fromJSON(json)).filter((v): v is VendorTeam => !!v);
  },
);
export const getTeamsByQuery = (interfaceName: string, workingForMe: boolean) => (state: RootState) =>
  selectTeamsByQuery(state, interfaceName, workingForMe);

export const getIsLoadingTeamsByQuery = (interfaceName: string, workingForMe: boolean) => (state: RootState) => {
  const key = `${interfaceName}:${workingForMe}`;
  return state.vendorTeams.byQuery.loading[key];
};
export const getErrorLoadingTeamsByQuery = (interfaceName: string, workingForMe: boolean) => (state: RootState) => {
  const key = `${interfaceName}:${workingForMe}`;
  return state.vendorTeams.byQuery.error[key];
};

export default vendorTeamsSlice;
